import dayjs from "dayjs";
import React, { FC, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useBestMatchPreferences } from "../../../../../../../../api/services/program/programApis";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../components";
import { MuiDateFieldFormController } from "../../../../../../../../components/dateField/MuiDateFieldFormController";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_30,
  SITE_CORE_COUNTRIES_VALUE,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  getTomorrowDate,
  setApiDataToDropDownOption,
  getValueOrBlank,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  ProgramDetail,
  OccupationalLevelOtherKey,
  OccupationalFunctionOtherKey,
  ParticipantTabFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { getFieldRuleByCountry } from "../../../../../../../../helpers/constants/countryFieldRulesUtils";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";

interface ParticipantSponsoringEmployerFormProps {
  pageId: string;
  isEditProgram?: boolean;
  programData?: ProgramDetail;
}

export const ParticipantSponsoringEmployer: FC<
  ParticipantSponsoringEmployerFormProps
> = ({ pageId, isEditProgram, programData }) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const { control, getValues, watch } = useFormContext();
  const dispatch = useDispatch();

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.sponsoringEmployer, key);
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programSiteCoreKeys.sponsoringEmployer, key);
  };

  const isDisabled = React.useMemo(() => {
    return (
      getValues(ParticipantTabFormKey.occupationalLevel) !==
      OccupationalLevelOtherKey
    );
  }, [watch(ParticipantTabFormKey.occupationalLevel)]);

  const isDisabledOccupationalFunction = React.useMemo(() => {
    return (
      getValues(ParticipantTabFormKey.occupationalFunction) !==
      OccupationalFunctionOtherKey
    );
  }, [watch(ParticipantTabFormKey.occupationalFunction)]);

  const tomorrowDate = getTomorrowDate();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const {
    data: bestMatchPreferencesList,
    isFetching: bestMatchPreferencesPending,
    isError: isBestMatchPreferencesError,
  } = useBestMatchPreferences();

  useEffect(() => {
    if (isBestMatchPreferencesError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }

    if (bestMatchPreferencesPending) {
      dispatch(
        toggleLoader({
          sponsoringPending: {
            isVisible: bestMatchPreferencesPending,
            message: commonLabels.loadingPleaseWait,
          },
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ sponsoringPending: { isVisible: false } }));
    };
  }, [isBestMatchPreferencesError, bestMatchPreferencesPending]);

  const deliveryCountry = useMemo(
    () => getValueOrBlank(programData?.deliveryCountry),
    [programData?.deliveryCountry],
  );

  const socialFundRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.socialFund,
  );

  const participantGroupTypeRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.participantGroupType,
  );

  const occupationalFunctionRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.occupationalFunction,
  );

  const currentEmploymentStartDateRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.currentEmploymentStartDate,
  );

  const totalYearsEmployedRule = getFieldRuleByCountry(
    deliveryCountry,
    ParticipantTabFormKey.totalYearsEmployed,
  );

  return (
    <MuiBox
      className="sponsoring-employer-details"
      data-testid="sponsoring-employer"
    >
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.clientName}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.clientName}`}
              label={getLabel(programSiteCoreKeys.clientName)}
              placeholder={commonLabels.selectPlaceHolder}
              disabled
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.jobTitle}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.jobTitle}`}
              label={getLabel(programSiteCoreKeys.jobTitle)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_100}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={commonValidationMessages.allowedSpecialCharactersTitle}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.employeeId}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.employeeId}`}
              label={getLabel(programSiteCoreKeys.employeeId)}
              shouldTrimSpaces={false}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_30}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              isMandatory={programData?.isEmployeeIdRequired || false}
              tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={commonValidationMessages.allowedSpecialCharactersTitle}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.employeeBand}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.employeeBand}`}
              label={getLabel(programSiteCoreKeys.employeeBand)}
              shouldTrimSpaces={false}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_30}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              isMandatory={programData?.isEmployeeBandRequired || false}
              tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
              tooltipHeaderTitle={commonValidationMessages.allowedSpecialCharactersTitle}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.totalYearsEmployed}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.totalYearsEmployed}`}
              label={getLabel(programSiteCoreKeys.totalYearsEmployed)}
              shouldTrimSpaces={false}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_30}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
              isMandatory={totalYearsEmployedRule.mandatory}
              type="number"
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDateFieldFormController
          control={control}
          name={ParticipantTabFormKey.currentEmploymentStartDate}
          label={getLabel(programSiteCoreKeys.currentEmploymentStartDate)}
          valueFormat={userCountryDateFormat}
          format={userCountryDateFormat}
          className="date-picker"
          id={`${pageId}-${ParticipantTabFormKey.currentEmploymentStartDate}`}
          value={
            getValues(ParticipantTabFormKey.currentEmploymentStartDate) !== ""
              ? dayjs(
                  getValues(ParticipantTabFormKey.currentEmploymentStartDate),
                  userCountryDateFormat,
                )
              : null
          }
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
          isMandatory={currentEmploymentStartDateRule.mandatory}
          clearable
        />
      </MuiGrid>
      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.occupationalFunction}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.occupationalFunction)}
              options={getDropdownValues(
                programSiteCoreKeys.occupationalFunctionList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.occupationalFunction}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
              isMandatory={occupationalFunctionRule.mandatory}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid
          size={gridSize}
          className={`${gridSizeMediumClass} occupational-function-other`}
        >
          <FormController
            control={control}
            name={ParticipantTabFormKey.occupationalFunctionOther}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.occupationalFunctionOther}`}
              label={getLabel(programSiteCoreKeys.occupationalFunctionOther)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_100}
              disabled={isDisabledOccupationalFunction}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>

      <MuiGrid container className="main-container">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ParticipantTabFormKey.occupationalLevel}
          >
            <MuiSelect
              label={getLabel(programSiteCoreKeys.occupationalLevel)}
              options={getDropdownValues(
                programSiteCoreKeys.occupationalLevelList,
              )}
              visibleDefaultSelectOption
              value=""
              id={`${pageId}-${ParticipantTabFormKey.occupationalLevel}`}
              placeholder={commonLabels.selectPlaceHolder}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid
          size={gridSize}
          className={`${gridSizeMediumClass} occupational-level-other`}
        >
          <FormController
            control={control}
            name={ParticipantTabFormKey.occupationalLevelOther}
          >
            <MuiTextField
              id={`${pageId}-${ParticipantTabFormKey.occupationalLevelOther}`}
              label={getLabel(programSiteCoreKeys.occupationalLevelOther)}
              placeholder={commonLabels.inputPlaceHolder}
              maxLength={MAX_LENGTH_100}
              disabled={isDisabled}
              showCloseIcon
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        {isEditProgram && (
          <>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <MuiDateFieldFormController
                control={control}
                name={ParticipantTabFormKey.planPostedDate}
                label={getLabel(programSiteCoreKeys.planPostedDate)}
                valueFormat={userCountryDateFormat}
                format={userCountryDateFormat}
                className="date-picker"
                minDate={tomorrowDate}
                id={`${pageId}-${ParticipantTabFormKey.planPostedDate}`}
                disableManualInput
                value={
                  getValues(ParticipantTabFormKey.planPostedDate) !== ""
                    ? dayjs(
                        getValues(ParticipantTabFormKey.planPostedDate),
                        userCountryDateFormat,
                      )
                    : null
                }
                requiredARIALabel={commonARIALabel.required}
                selectDateARIALabel={commonARIALabel.selectDate}
              />
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={ParticipantTabFormKey.bestMatchPreference}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.bestMatchPreference)}
                  options={setApiDataToDropDownOption(bestMatchPreferencesList)}
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${ParticipantTabFormKey.bestMatchPreference}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </>
        )}

        {programData?.deliveryCountry ===
          SITE_CORE_COUNTRIES_VALUE.COUNTRY_BELGIUM && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.participantGroupType}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.participantGroupType)}
                options={fetchDropdownValues(
                  programSiteCoreKeys.sponsoringEmployer,
                  programSiteCoreKeys.participantGroupTypeList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.participantGroupType}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                isMandatory={participantGroupTypeRule.mandatory}
              />
            </FormController>
          </MuiGrid>
        )}

        {programData?.deliveryCountry ===
          SITE_CORE_COUNTRIES_VALUE.COUNTRY_BELGIUM && (
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.socialFund}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.socialFund)}
                options={fetchDropdownValues(
                  programSiteCoreKeys.sponsoringEmployer,
                  programSiteCoreKeys.socialFundList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.socialFund}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                isMandatory={socialFundRule.mandatory}
              />
            </FormController>
          </MuiGrid>
        )}

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDateFieldFormController
            control={control}
            name={ParticipantTabFormKey.datePlanApproved}
            label={getLabel(programSiteCoreKeys.datePlanApproved)}
            valueFormat={userCountryDateFormat}
            format={userCountryDateFormat}
            className="date-picker"
            id={`${pageId}-${ParticipantTabFormKey.datePlanApproved}`}
            value={
              getValues(ParticipantTabFormKey.datePlanApproved) !== ""
                ? dayjs(
                    getValues(ParticipantTabFormKey.datePlanApproved),
                    userCountryDateFormat,
                  )
                : null
            }
            requiredARIALabel={commonARIALabel.required}
            selectDateARIALabel={commonARIALabel.selectDate}
            clearable
          />
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
